import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Typography } from '@mui/joy';

import { useCopy } from '@/components/CopyButton/useCopy';
import { SharePanel } from '@/components/RunsTable/components/ShareAction';
import {
  useShareDataset,
  useShareDatasetMutation,
  useUnshareDatasetMutation,
} from '@/hooks/useSwr';
import { DatasetSchema } from '@/types/schema';
import { appPublicDatasetsPath, appPublicPath } from '@/utils/constants';

export function ShareDatasetAction(props: {
  dataset: DatasetSchema;
  variant?: 'icon' | 'button' | 'custom';
  customButton?: (icon: React.ReactNode, isPublic: boolean) => React.ReactNode;
  bypassPortal?: boolean;
}) {
  const share = useShareDataset(props.dataset.id);

  const shareMutation = useShareDatasetMutation(props.dataset.id);
  const unshareMutation = useUnshareDatasetMutation(props.dataset.id);

  const url = `/${appPublicPath}/${share.data?.share_token}/${appPublicDatasetsPath}`;
  const readableUrl = `${share.data?.share_token}/${appPublicDatasetsPath}`;

  const { copied, onCopy } = useCopy({ copy: window.location.origin + url });

  return (
    <SharePanel
      share={share}
      shareMutation={shareMutation}
      unshareMutation={unshareMutation}
      url={url}
      readableUrl={readableUrl}
      copied={copied}
      onCopy={onCopy}
      variant={props.variant}
      customButton={props.customButton}
      bypassPortal={props.bypassPortal}
      disclaimer={
        <div className="flex flex-col gap-1">
          <Typography fontWeight={600}>Dataset Shared</Typography>
          <Typography color="neutral" fontSize="md">
            Anyone with the link below can view and use this dataset and all the
            examples associated with it.
          </Typography>
          <div className="my-3 inline-flex items-center justify-start gap-5 rounded-xl border border-secondary border-opacity-60 bg-secondary bg-opacity-60 px-5 py-4">
            <div className="relative h-5 w-5 rounded-[20px]">
              <div className="absolute left-[-4px] top-[-4px] h-7 w-7 rounded-3xl border-2 border-brand opacity-30" />
              <div className="absolute left-[-9px] top-[-9px] h-[38px] w-[38px] rounded-3xl border-2 border-brand opacity-10" />
              <div className="absolute left-0 top-0 h-5 w-5" />
              <InformationCircleIcon className="absolute left-0 top-0 h-5 w-5 stroke-brand-green-400" />
            </div>
            <div className="inline-flex shrink grow basis-0 flex-col items-start justify-start gap-1">
              <div className="text-sm font-semibold leading-tight text-opacity-90">
                Shares Full Dataset and Associated Experiments
              </div>
              <div className="self-stretch text-sm font-normal leading-tight text-tertiary">
                This link gives viewers access to all example rows, experiments,
                and associated runs and feedback on this dataset.
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
}
